import React from "react";

import defaultImage from "@images/seo/default-link-image.jpg";
import defaultImage2x from "@images/seo/default-link-image_@2x.jpg";
import { Link } from "gatsby";
import { Navigation } from "swiper";
import { Swiper, SwiperSlide } from "swiper/react";

import { Icon, IconSizeEnum, IconTypeEnum } from "@/components/common/icon";
import { SITE_DATE_TIME_ZONE } from "@/constants";
import { convertToDateLiteraryShortened } from "@/utils/dateUtils";
import { toThumborWebpSrcSet } from "@/utils/seo/thumbor";

import { getReadDurationMinByText, shuffleArrayById } from "../utils";

import { CAROUSEL_CARD_IMAGE_SLOT_WIDTH } from "./constants";
import { CarouselLinksBlockProps } from "./types";

import "swiper/css";
import "./styles.scss";

const CarouselLinksBlock = ({ autolinkedArticleLinks, urlPrefix, seoId }: CarouselLinksBlockProps) =>
  (
    <div className="carousel-links-block">
      <div className="carousel-links-block__arrow left">
        <Icon type={IconTypeEnum.Left} />
      </div>
      <div className="carousel-links-block__arrow right">
        <Icon type={IconTypeEnum.Right} />
      </div>

      <Swiper
        modules={[Navigation]}
        slidesPerView="auto"
        spaceBetween={16}
        breakpoints={{
          768: {
            slidesPerView: 3.3,
          },
          930: {
            slidesPerView: 4.2,
          },
          1200: {
            slidesPerView: 5,
          },
        }}
        navigation={{
          nextEl: ".carousel-links-block__arrow.right",
          prevEl: ".carousel-links-block__arrow.left",
          disabledClass: "hide",
        }}
        className="carousel-links-block__slider"
      >
        {shuffleArrayById(autolinkedArticleLinks, seoId)?.map((item) => {
          const currentBreadcrumbs = item?.breadcrumbs;
          const firstItemBreadcrumbs = currentBreadcrumbs?.length
            ? currentBreadcrumbs[0]
            : null;
          const breadcrumbDisplayName = firstItemBreadcrumbs?.article?.breadcrumbDisplayName;
          const minToRead = getReadDurationMinByText(item?.text ?? "");
          const imageUrl = item?.image?.twoX?.url ?? defaultImage2x;
          // Ранее третий кандидат писался как `url, 3x` — лишняя запятая делала его
          // невалидным, браузер молча его отбрасывал (и webp-source не строился совсем).
          const srcSet = [
            `${item?.image?.twoX?.url ?? defaultImage}`,
            `${imageUrl} 2x`,
            `${item?.image?.threeX?.url ?? imageUrl} 3x`,
          ].join(", ");
          // Авто-WebP через Thumbor (#11, часть B). null → source не рендерим, остаётся оригинал.
          const webpSrcSet = toThumborWebpSrcSet(srcSet, CAROUSEL_CARD_IMAGE_SLOT_WIDTH);

          return (
            <SwiperSlide key={item?.documentId} className="carousel-links-block__card-container">
              <Link to={`/${urlPrefix}${item?.URL}`}>
                <div className="carousel-links-block__card">
                  <picture>
                    {/* webp идёт первым — браузер выбирает его, если поддерживает */}
                    {webpSrcSet && <source type="image/webp" srcSet={webpSrcSet} />}
                    <source srcSet={srcSet} media="(max-width: 300px)" />
                    <img
                      src={imageUrl}
                      alt={item?.h1 || ""}
                      loading="lazy"
                      decoding="async"
                    />
                  </picture>
                </div>
                <div className="carousel-links-block__details-header-block">
                  <p className="carousel-links-block__parent">{breadcrumbDisplayName}</p>
                  {/* `span`, а не `Link`: вся карточка уже ссылка, а `<a>` внутри `<a>` браузер разбивает */}
                  <span className="carousel-links-block__title">{item?.h1 || ""}</span>
                </div>
                <div className="carousel-links-block__details">
                  <p className="carousel-links-block__published-date">
                    {convertToDateLiteraryShortened(item?.publishedAt, SITE_DATE_TIME_ZONE)}
                  </p>
                  <div className="carousel-links-block__data-item">
                    <Icon type={IconTypeEnum.Time} size={IconSizeEnum.Size20} />
                    <div>
                      {minToRead}
                      {" "}
                      мин
                    </div>
                  </div>
                </div>
              </Link>
            </SwiperSlide>
          );
        })}
      </Swiper>
    </div>
  );
export default CarouselLinksBlock;
